// External Libraries
import { Component, OnDestroy, OnInit, computed, signal, inject } from '@angular/core';
import { Subscription, firstValueFrom, takeUntil, Subject } from 'rxjs';
import { Router, RouterOutlet } from '@angular/router';

// Frontegg Services
import { FronteggAuthService } from '@frontegg/angular';

// Project Services
import { AuthService } from '../../services/auth/auth.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { SignOutService } from '../../services/sign-out-state/sign-out-state.service';
import { UserPrivilegesService } from '../../services/auth-privileges/auth-privileges.service';
import { TenantApiService } from '../../services/tenant-api/tenant-api.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { WhatFixService } from '../../services/whatFix/whatfix.service';
import { BannerComponent } from '../../components/banner/banner.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { UrlVerificationComponent } from '@ai-document-processor/shared-libraries';

// Constants
const FEATURE_FLAGS = {
  WEB_CAPTURE_ACCESS: 'web-capture-access',
  LICENSE_IDP: 'license-IDP',
  LICENSE_ITP: 'license-itp'
} as const;

const ROLE_KEYS = {
  PLATFORM_ADMIN: 'Platform Admin'
} as const;

const PRIVILEGE_CODES = {
  WEB_CAPTURE_ACCESS: 'WEB_CAPTURE_ACCESS'
} as const;

// Interfaces
interface MenuSection {
  title: string;
  items: Array<{ label: string; route: string }>;
}

interface Role {
  key: string;
}

interface FronteggUser {
  accessToken: string;
  customClaims?: {
    principal_id?: string;
  };
  roles: Role[];
}

interface AuthState {
  isAuthenticated: boolean;
  user?: FronteggUser | null;
}

interface UserPrivileges {
  applicationPrivileges: Array<{ code: string }>;
}

@Component({
  selector: 'app-intelligence',
  templateUrl: './intelligence.component.html',
  styleUrl: './intelligence.component.scss',
  imports: [
    BannerComponent,
    SidebarComponent,
    RouterOutlet,
    TranslatePipe,
    UrlVerificationComponent
  ]
})
export class IntelligenceComponent implements OnInit, OnDestroy {
  private readonly fronteggAuthService = inject(FronteggAuthService);
  private readonly authService = inject(AuthService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly router = inject(Router);
  private readonly signOutService = inject(SignOutService);
  private readonly userPrivilegesService = inject(UserPrivilegesService);
  readonly tenantService = inject(TenantApiService);
  private readonly translate = inject(TranslateService);
  private readonly whatFix = inject(WhatFixService);

  readonly isAccessFlagLoading = signal(true);
  readonly isAuthReady = signal(false);
  readonly isUrlVerified = signal(false);
  readonly isAccessEnabled = signal<boolean | null>(null);
  readonly user = signal<FronteggUser | null>(null);
  readonly menuSections = signal<MenuSection[]>([]);

  isAccessIDPMgmt = false;
  isAutoClassificationEnabled = false;
  featureFlagErrorText = '';
  userId = '';
  tenantId = '';
  currentTab = '';
  readonly transcriptProcessingFlag = signal(false);
  readonly webCaptureFlag = signal(false);
  readonly hasWebCapturePrivilege = signal(false);
  readonly hasWebCapture = computed(() => this.hasWebCapturePrivilege() && this.webCaptureFlag());
  readonly hasTranscriptProcessing = computed(
    () =>
      this.transcriptProcessingFlag() &&
      this.userPrivilegesService.hasItpProcessorRole(this.user()?.roles)
  );
  isWebApps = true;
  userPrincipalId = '';
  itpPrivilege = false;

  private readonly destroy$ = new Subject<void>();

  /**
   * Angular lifecycle hook that is called after component initialization.
   */
  ngOnInit(): void {
    this.checkFeatureFlag();
    this.setupSignOutSubscription();
  }

  /**
   * Sets up subscription to sign-out events and handles automatic sign-out if needed.
   */
  private setupSignOutSubscription(): void {
    this.signOutService.signOut$.pipe(takeUntil(this.destroy$)).subscribe(signOut => {
      if (signOut && !this.signOutService.isFilingContent) {
        this.signOutService.signOut();
      }
    });
  }

  /**
   * Handles errors by logging sanitized messages to the console.
   * @param message - Description of the error context
   * @param error - The error object or value
   */
  private handleError(message: string, error: unknown): void {
    const sanitize = (input: string) => input.replace(/[\x00-\x1F\x7F]/g, ' ');
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`${sanitize(message)}: ${sanitize(errorMessage)}`);
  }

  /**
   * Retrieves tenant and organization context from localStorage.
   */
  private getTenantContext(): { tenantId: string | null; organization: string | null } {
    return {
      tenantId: localStorage.getItem('tenantId'),
      organization: localStorage.getItem('vanityName')
    };
  }

  /**
   * Waits for an authenticated Frontegg state then runs the feature flag + auth flow.
   */
  checkFeatureFlag(): void {
    const { tenantId } = this.getTenantContext();

    this.fronteggAuthService.authState$.pipe(takeUntil(this.destroy$)).subscribe(authState => {
      if (!authState.isAuthenticated || !authState.user?.accessToken) return;

      const fronteggUser = authState.user as unknown as FronteggUser;
      this.user.set(fronteggUser);
      this.userPrincipalId = fronteggUser.customClaims?.principal_id || '';

      if (tenantId) {
        this.handleFeatureFlagInit(tenantId, this.userPrincipalId, () => {
          this.isUrlVerified.set(true);
          this.tenantId = tenantId;
          this.whatFix.initializeWithContext(this.tenantId, this.userPrincipalId);
          const currentUser = this.user();
          if (currentUser) {
            this.handleAuthenticatedUser(currentUser);
          }
        });
      } else {
        this.isAccessFlagLoading.set(false);
        this.isUrlVerified.set(false);
        this.isAccessEnabled.set(null);
      }
    });
  }

  /**
   * Initializes feature flags and calls onSuccess if access is granted.
   * @param tenantId
   * @param userId
   * @param onSuccess
   */
  private async handleFeatureFlagInit(
    tenantId: string,
    userId: string,
    onSuccess: () => void
  ): Promise<void> {
    this.isAccessFlagLoading.set(true);
    try {
      await this.featureFlagService.initialize({ userId, tenantId });

      const [itpFlagValue, webCaptureFlagValue] = await Promise.all([
        firstValueFrom(this.featureFlagService.getFlag(FEATURE_FLAGS.LICENSE_ITP, false)),
        firstValueFrom(this.featureFlagService.getFlag(FEATURE_FLAGS.WEB_CAPTURE_ACCESS, false))
      ]);
      this.transcriptProcessingFlag.set(itpFlagValue);
      this.webCaptureFlag.set(webCaptureFlagValue);
      const isItpAdmin = this.userPrivilegesService.hasItpAdminRole(this.user()?.roles);

      if (itpFlagValue && isItpAdmin) {
        this.isAccessFlagLoading.set(false);
        this.isAccessEnabled.set(true);
        onSuccess();
        return;
      }

      this.isAccessFlagLoading.set(false);
      if (webCaptureFlagValue) {
        this.isAccessEnabled.set(true);
        onSuccess();
      } else {
        this.setAccessDeniedState();
      }
    } catch (error) {
      this.handleError('Feature flag initialization failed', error);
      this.setAccessDeniedState();
    }
  }

  /**
   * Sets the state to indicate access is denied.
   */
  private setAccessDeniedState(): void {
    this.isAccessFlagLoading.set(false);
    this.isUrlVerified.set(false);
    this.isAccessEnabled.set(false);
    this.featureFlagErrorText = 'webCaptureFeatureFlagError';
  }

  /**
   * Builds menu sections based on user privileges.
   * - Platform Admin (with license-IDP flag): sees IDP Management only
   * - ITP Admin: sees ITP Management only
   * - Both roles: sees both sections
   */
  private buildMenuSections(): MenuSection[] {
    const sections: MenuSection[] = [];

    if (this.isAccessIDPMgmt) {
      sections.push({
        title: this.translate.instant('idpManagement'),
        items: [{ label: this.translate.instant('usage'), route: '/intelligence/idpmanagement' }]
      });
    }

    if (this.itpPrivilege) {
      sections.push({
        title: this.translate.instant('itpManagement.itpManagement'),
        items: [
          {
            label: this.translate.instant('itpManagement.sisIntegrationMapping'),
            route: '/intelligence/itpmanagement'
          }
        ]
      });
    }

    return sections;
  }

  /**
   * Checks if user can access Intelligence Settings page.
   * User must have either Platform Admin (with IDP flag) or ITP Admin role.
   */
  get canAccessIntelligenceSettings(): boolean {
    return this.isAccessIDPMgmt || this.itpPrivilege;
  }

  /**
   * Re-verifies the tenant URL and triggers feature flag check based on the tenant ID.
   */
  async onSuccessfulVerification(): Promise<void> {
    const { tenantId } = this.getTenantContext();
    if (tenantId) {
      this.isAccessFlagLoading.set(true);
      await this.handleFeatureFlagInit(tenantId, this.userPrincipalId, () => {
        this.isUrlVerified.set(true);
        this.tenantId = tenantId;
        this.whatFix.initializeWithContext(this.tenantId, this.userPrincipalId);
        const currentUser = this.user();
        if (currentUser) {
          this.handleAuthenticatedUser(currentUser);
        }
      });
    }
  }

  /**
   * Initializes auto-classification feature flag for the current tenant.
   */
  initializeAutoClassificationFlag(): void {
    const { tenantId } = this.getTenantContext();
    if (tenantId) {
      this.featureFlagService
        .getFlag(FEATURE_FLAGS.LICENSE_IDP, false)
        .pipe(takeUntil(this.destroy$))
        .subscribe(flagValue => {
          this.isAutoClassificationEnabled = Boolean(flagValue);
          this.isAccessIDPMgmt =
            this.checkPlatformAdmin(this.user()?.roles || []) && Boolean(flagValue);
        });
    }
  }

  /**
   * Check if user has Platform Admin role.
   * @param roles - Array of user roles
   */
  checkPlatformAdmin(roles: Role[]): boolean {
    return roles.some(role => role.key === ROLE_KEYS.PLATFORM_ADMIN);
  }

  /**
   * Handles authenticated user state, sets auth token, initializes flags, and checks privileges.
   * @param user - Authenticated FronteggUser
   */
  private async handleAuthenticatedUser(user: FronteggUser): Promise<void> {
    this.user.set(user);
    this.authService.setAuthToken(user.accessToken);
    this.initializeAutoClassificationFlag();

    await this.checkUserPrivileges(user.accessToken);
    this.isAuthReady.set(true);
  }

  /**
   * Redirects the user to the login page with the specified tenant id and organization.
   * @param tenantId - Tenant identifier
   * @param organization - Organization name
   */
  loginWithRedirect(tenantId: string, organization: string): void {
    this.fronteggAuthService.loginWithRedirect({ tenantId, organization });
  }

  /**
   * Signs out the user and sets sign-out state.
   */
  signOut(): void {
    this.signOutService.setSignOutState(true);
  }

  /**
   * Angular lifecycle hook that is called when the component is destroyed.
   * Cleans up all active subscriptions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Checks user privileges and navigates to unauthorized page if access is denied.
   * Validates the user has at least one of:
   * - Platform Admin role + license-IDP flag + WEB_CAPTURE_ACCESS privilege (IDP Management)
   * - ITP Admin role (ITP Management)
   * @param accessToken - User access token
   */
  public async checkUserPrivileges(accessToken: string): Promise<void> {
    try {
      const privileges = await this.userPrivilegesService.getUserPrivileges(accessToken);
      this.userPrivilegesService.setPrivileges(privileges);

      const hasWebCaptureAccess = this.hasWebCaptureAccess(privileges);
      this.hasWebCapturePrivilege.set(hasWebCaptureAccess);
      this.itpPrivilege = this.userPrivilegesService.hasItpAdminRole(this.user()?.roles);

      const idpFlagValue = await this.getFeatureFlagSafely(FEATURE_FLAGS.LICENSE_IDP);
      this.isAccessIDPMgmt = this.checkPlatformAdmin(this.user()?.roles || []) && idpFlagValue;

      this.menuSections.set(this.buildMenuSections());

      const canUseIdp = this.isAccessIDPMgmt && hasWebCaptureAccess;
      const canUseItp = this.itpPrivilege;
      if (!canUseIdp && !canUseItp) {
        await this.navigateToUnauthorized();
      }
    } catch (error) {
      this.handleError('Error checking user privileges', error);
      await this.navigateToUnauthorized();
    }
  }

  /**
   * Checks if the user has web capture access privilege.
   * @param privileges - UserPrivileges object
   */
  private hasWebCaptureAccess(privileges: UserPrivileges): boolean {
    return privileges.applicationPrivileges.some(
      privilege => privilege.code === PRIVILEGE_CODES.WEB_CAPTURE_ACCESS
    );
  }

  /**
   * Safely retrieves a feature flag value, handling errors consistently.
   * @param flagKey - Feature flag key
   * @returns Promise<boolean> - The flag value or false if error occurs
   */
  private async getFeatureFlagSafely(flagKey: string): Promise<boolean> {
    try {
      return await firstValueFrom(this.featureFlagService.getFlag(flagKey, false));
    } catch (error) {
      this.handleError(`Could not read ${flagKey} feature flag`, error);
      return false;
    }
  }

  /**
   * Navigates to the unauthorized page.
   */
  private async navigateToUnauthorized(): Promise<void> {
    await this.router.navigate(['unauthorized']);
  }
}
